import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useServices } from "@/hooks/useServices";
import { PackageData } from "@/pages/CreatePackage";
import type { Service } from "@/types/service";
import { Package, ArrowLeft, ArrowRight } from "lucide-react";
import { ProgressSteps } from "./ProgressSteps";
interface ServicesStepProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  onPrev: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}
export const ServicesStep = ({
  data,
  onUpdate,
  onNext,
  onPrev,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: ServicesStepProps) => {
  const {
    data: allServices,
    isLoading
  } = useServices();
  const handleServiceChange = (item: Service, checked: boolean) => {
    const currentServices = data.services || [];
    const newServices = checked ? [...currentServices, item] : currentServices.filter(s => s.id !== item.id);
    
    // تحديث حالة تفعيل الخدمات بناء على الخدمات المختارة
    const hasServices = newServices.length > 0;
    
    onUpdate({
      services: newServices,
      includesServices: hasServices
    });
  };
  const isSelected = (item: Service) => {
    return !!data.services?.find(s => s.id === item.id);
  };
  const renderServices = () => {
    if (isLoading) {
      return Array.from({
        length: 5
      }).map((_, i) => <div key={i} className="flex items-center space-x-2 space-x-reverse my-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-[250px]" />
        </div>);
    }
    if (!allServices || allServices.length === 0) {
      return <p className="text-gray-600">لا توجد خدمات متاحة حالياً.</p>;
    }
    return allServices.map(service => <div key={service.id} className="flex items-center space-x-2 space-x-reverse">
        <Checkbox id={`service-${service.id}`} checked={isSelected(service)} onCheckedChange={checked => handleServiceChange(service, !!checked)} />
        <Label htmlFor={`service-${service.id}`} className="font-normal cursor-pointer">
          {service.name_ar}
        </Label>
      </div>);
  };
  return <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5" />
          الخدمات المشمولة
        </CardTitle>
        
      </CardHeader>
      <CardContent className="space-y-6">
        {/* مربع الخدمات المتصل بالخطوات */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-blue-800">اختر الخدمات الإضافية</h3>
            <div className="text-sm text-blue-600">
              {data.services?.length || 0} خدمة مختارة
            </div>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {renderServices()}
          </div>
          
          {/* معلومات توضيحية */}
          <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-blue-700">
              💡 عند اختيار خدمات، ستصبح خطوة "الخدمات المشمولة" مفعلة في شريط التقدم السفلي
            </p>
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t">
          <Button variant="outline" onClick={onPrev}>
            <ArrowRight className="w-4 h-4 ml-2" />
            السابق
          </Button>
          <Button onClick={onNext} className="bg-blue-600 hover:bg-blue-700">
            التالي
            <ArrowLeft className="w-4 h-4 mr-2" />
          </Button>
        </div>
      </CardContent>
      
      {/* Progress Steps */}
      <ProgressSteps 
        currentStep={7} 
        completedSteps={completedSteps} 
        onStepClick={onStepClick} 
        isStepRequired={isStepRequired} 
      />
    </Card>;
};